import * as THREE from 'three';
import { BlendFunction, BloomEffect, Effect, EffectAttribute, EffectComposer, EffectPass, RenderPass } from 'postprocessing';
import type { Mood } from './mood';
import type { PostPipeline, Quality } from './post';

/**
 * The post stack on pmndrs `postprocessing` (docs/PERF.md "Post stack: pmndrs"): the same picture as the
 * three.js-addons chain it replaces, in fewer full-screen passes. HDR render (MSAA) -> ONE merged
 * EffectPass that adds the bloom, grades (linear), tone maps (ACES + sRGB) and finishes (vignette,
 * halftone in the deep shadows, grain; HIGH adds the slight edge aberration). The bloom is pmndrs'
 * mipmap blur: a threshold pass at reduced size and a down / up mip chain, read once by the merged pass.
 * 'low' still renders directly with no post.
 *
 * Passes per frame at full size: RenderPass + EffectPass (HIGH and MEDIUM alike). The addons chain
 * drew 3 (MEDIUM: scene, bloom blend, finish) or 5 (HIGH: scene, bloom blend, grade, output, film).
 */

/** MSAA samples of the HDR target per tier (MSAA is resolved on-tile on phones: cheaper than SMAA's 3 passes). */
const SAMPLES: Record<Quality, number> = { low: 0, medium: 2, high: 4 };
/** Size of the bloom threshold image relative to the frame; the mip chain starts at half of it. */
const BLOOM_SCALE: Record<Quality, number> = { low: 0, medium: 0.25, high: 0.5 };
/** Mip levels of the bloom blur (fewer from MEDIUM's smaller start: the tail is the same few pixels). */
const BLOOM_LEVELS: Record<Quality, number> = { low: 0, medium: 5, high: 6 };
/**
 * UnrealBloomPass summed its five blurred mips with weights ~0.52..0.68 (radius 0.6), about 3x the
 * mood's bloom strength; pmndrs' mip blur is normalised, so the mood value is scaled to match.
 */
const BLOOM_GAIN = 3;

/** Grade + ACES + sRGB + film, as post.ts's FinishShader, as one pmndrs effect. */
const finishFragment = /* glsl */ `
  uniform float uSaturation, uContrast, uExposure, uToneExposure;
  uniform vec3 uShadowTint, uHighTint;
  uniform float uTime, uGrain, uVignette, uHalftone;
  vec3 rrtOdtFit(vec3 v) {
    vec3 a = v * (v + 0.0245786) - 0.000090537;
    vec3 b = v * (0.983729 * v + 0.4329510) + 0.238081;
    return a / b;
  }
  vec3 acesFilmic(vec3 color) {
    const mat3 inM = mat3(vec3(0.59719, 0.07600, 0.02840), vec3(0.35458, 0.90834, 0.13383), vec3(0.04823, 0.01566, 0.83777));
    const mat3 outM = mat3(vec3(1.60475, -0.10208, -0.00327), vec3(-0.53108, 1.10813, -0.07276), vec3(-0.07367, -0.00605, 1.07602));
    color *= uToneExposure / 0.6;
    return clamp(outM * rrtOdtFit(inM * color), 0.0, 1.0);
  }
  float grainHash(vec2 p) { return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
  void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {
    vec3 c = inputColor.rgb * uExposure;
    float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
    c = mix(vec3(l), c, uSaturation);
    c *= mix(uShadowTint, uHighTint, smoothstep(0.02, 0.5, l));
    c = max(c, vec3(0.0));
    c = 0.18 * pow(c / 0.18 + 1e-5, vec3(uContrast));
    vec3 col = sRGBTransferOETF(vec4(acesFilmic(c), 1.0)).rgb;
    vec2 d = uv - 0.5;
    float v = smoothstep(0.95, 0.25, length(d * vec2(1.0, 1.25)));
    col *= mix(1.0 - uVignette, 1.0, v);
    float lum = dot(col, vec3(0.299, 0.587, 0.114));
    vec2 px = gl_FragCoord.xy;
    vec2 rot = mat2(0.7071, -0.7071, 0.7071, 0.7071) * px / 4.0;
    vec2 cell = fract(rot) - 0.5;
    float dotMask = smoothstep(0.32, 0.22, length(cell));
    float shadow = 1.0 - smoothstep(0.02, 0.2, lum);
    col *= 1.0 - uHalftone * shadow * (1.0 - dotMask);
    float n = grainHash(px + fract(uTime) * 91.7) - 0.5;
    col += n * uGrain * (0.6 + 0.4 * (1.0 - lum));
    outputColor = vec4(col, 1.0);
  }`;

/** HIGH's slight edge aberration: red and blue read a touch apart towards the corners (HDR input). */
const aberrationFragment = /* glsl */ `
  uniform float uAberration;
  void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {
    vec2 d = uv - 0.5;
    vec2 o = d * uAberration * dot(d, d) * 4.0;
    outputColor = vec4(texture2D(inputBuffer, uv - o).r, inputColor.g, texture2D(inputBuffer, uv + o).b, inputColor.a);
  }`;

class FinishEffect extends Effect {
  constructor(q: Quality) {
    const u = (v: unknown): THREE.Uniform => new THREE.Uniform(v);
    super('FinishEffect', finishFragment, {
      blendFunction: BlendFunction.SRC,
      uniforms: new Map<string, THREE.Uniform>([
        ['uSaturation', u(0.9)],
        ['uContrast', u(1.04)],
        ['uShadowTint', u(new THREE.Vector3(0.88, 0.97, 1.1))],
        ['uHighTint', u(new THREE.Vector3(1.08, 1.0, 0.88))],
        ['uExposure', u(1.0)],
        ['uToneExposure', u(1.0)],
        ['uTime', u(0)],
        ['uGrain', u(q === 'high' ? 0.03 : 0.02)],
        ['uVignette', u(0.34)],
        ['uHalftone', u(q === 'high' ? 0.09 : 0.06)],
      ]),
    });
    // The output is display-referred (sRGB) already; the pass must not encode it again.
    this.outputColorSpace = THREE.SRGBColorSpace;
  }
}

class AberrationEffect extends Effect {
  constructor() {
    super('AberrationEffect', aberrationFragment, {
      attributes: EffectAttribute.CONVOLUTION,
      blendFunction: BlendFunction.SRC,
      uniforms: new Map<string, THREE.Uniform>([['uAberration', new THREE.Uniform(0.0012)]]),
    });
  }
}

export function createPmndrsPipeline(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera): PostPipeline {
  let composer: EffectComposer | null = null;
  let scenePass: RenderPass | null = null;
  let bloom: BloomEffect | null = null;
  let finish: FinishEffect | null = null;
  let mood: Mood | null = null;
  let quality: Quality = 'high';
  const size = new THREE.Vector2();
  let lastW = 0;
  let lastH = 0;

  const applyMood = (): void => {
    if (!mood) return;
    if (bloom) {
      bloom.intensity = mood.bloom * BLOOM_GAIN;
      bloom.mipmapBlurPass.radius = mood.bloomRadius;
      bloom.luminanceMaterial.threshold = mood.bloomThreshold;
    }
    if (!finish) return;
    const u = finish.uniforms;
    u.get('uSaturation')!.value = mood.grade.saturation;
    u.get('uContrast')!.value = mood.grade.contrast;
    (u.get('uShadowTint')!.value as THREE.Vector3).set(...mood.grade.shadowTint);
    (u.get('uHighTint')!.value as THREE.Vector3).set(...mood.grade.highTint);
  };

  const disposePasses = (): void => {
    composer?.dispose();
    composer = null;
    scenePass = null;
    bloom = null;
    finish = null;
    // pmndrs' composer turns autoClear off; the direct LOW path needs it back.
    renderer.autoClear = true;
  };

  const build = (): void => {
    disposePasses();
    renderer.shadowMap.enabled = quality !== 'low';
    renderer.shadowMap.needsUpdate = true;
    if (quality === 'low') return;
    composer = new EffectComposer(renderer, { frameBufferType: THREE.HalfFloatType, multisampling: SAMPLES[quality], stencilBuffer: false });
    scenePass = new RenderPass(scene, camera);
    composer.addPass(scenePass);
    bloom = new BloomEffect({
      blendFunction: BlendFunction.ADD,
      luminanceThreshold: 1.0,
      luminanceSmoothing: 0.01,
      mipmapBlur: true,
      intensity: 0.45 * BLOOM_GAIN,
      radius: 0.6,
      levels: BLOOM_LEVELS[quality],
    });
    bloom.luminancePass.resolution.scale = BLOOM_SCALE[quality];
    finish = new FinishEffect(quality);
    const effects: Effect[] = quality === 'high' ? [new AberrationEffect(), bloom, finish] : [bloom, finish];
    composer.addPass(new EffectPass(camera, ...effects));
    renderer.getSize(size);
    composer.setSize(size.x, size.y, false);
    lastW = size.x;
    lastH = size.y;
    applyMood();
  };
  build();

  return {
    get quality() {
      return quality;
    },
    setMood(m) {
      mood = m;
      applyMood();
    },
    setQuality(q) {
      if (q === quality && (composer || q === 'low')) return;
      quality = q;
      build();
    },
    setPixelRatio(pr) {
      if (Math.abs(renderer.getPixelRatio() - pr) < 0.005) return;
      renderer.setPixelRatio(pr);
      if (!composer) return;
      renderer.getSize(size);
      composer.setSize(size.x, size.y, false);
      lastW = size.x;
      lastH = size.y;
    },
    rebuild() {
      build();
    },
    render(sc, cam, time) {
      renderer.info.reset();
      if (!composer) {
        renderer.render(sc, cam);
        return;
      }
      renderer.getSize(size);
      if (size.x !== lastW || size.y !== lastH) {
        composer.setSize(size.x, size.y, false);
        lastW = size.x;
        lastH = size.y;
      }
      if (scenePass) {
        scenePass.mainScene = sc;
        scenePass.mainCamera = cam;
      }
      if (finish) {
        finish.uniforms.get('uTime')!.value = time;
        finish.uniforms.get('uToneExposure')!.value = renderer.toneMappingExposure;
      }
      composer.render(0);
    },
    targetBytes() {
      if (!composer) return 0;
      const pr = renderer.getPixelRatio();
      renderer.getSize(size);
      const px = Math.round(size.x * pr) * Math.round(size.y * pr);
      // RGBA16F (8 B/px): the MSAA input target (multisampled colour + depth, resolved texture) and
      // the composer's output ping-pong target.
      const samples = SAMPLES[quality];
      const msaa = px * samples * (8 + 4);
      const resolved = px * (8 + 4);
      const pingPong = px * 8;
      // Bloom: threshold image, then down + up mips from half of it.
      let mip = px * BLOOM_SCALE[quality] * BLOOM_SCALE[quality];
      let bloomBytes = mip * 8;
      for (let i = 0; i < BLOOM_LEVELS[quality]; i++) {
        mip /= 4;
        bloomBytes += 2 * mip * 8;
      }
      return Math.round(msaa + resolved + pingPong + bloomBytes);
    },
    dispose() {
      disposePasses();
    },
  };
}
